import { FormManagementQuestionCharts } from '@/components/Forms/management/FormManagementQuestionCharts'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  FormManagementMetricTone,
  FormManagementWorkspaceData,
} from '@/contracts/form-management'
import type { AnalyticsExportServerRequest } from '@/contracts/analytics-export-server'
import {
  type AnalyticsExportFile,
  type AnalyticsExportFormat,
  buildAnalyticsExportFile,
  downloadAnalyticsExportFile,
} from '@/lib/analytics-export'
import { buildAnalyticsExportAuditEventPreview } from '@/lib/analytics-export-audit-event-preview'
import { buildAnalyticsExportDownloadStatePreview } from '@/lib/analytics-export-download-state-preview'
import { buildAnalyticsExportErrorStatePreview } from '@/lib/analytics-export-error-state-preview'
import {
  type AnalyticsExportRequestRangeId,
  buildAnalyticsExportRequest,
} from '@/lib/analytics-export-request'
import { buildAnalyticsExportResponsePreview } from '@/lib/analytics-export-response-preview'
import {
  type AnalyticsExportDownloadReadiness,
  type AnalyticsExportJobClient,
  type AnalyticsExportJobSafeResponse,
  analyticsExportJobClient as defaultAnalyticsExportJobClient,
} from '@/lib/data-client/analytics'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import {
  BarChart3,
  CalendarDays,
  Download,
  FileJson,
  FileSpreadsheet,
  PieChart,
  TrendingUp,
} from 'lucide-react'
import { useMemo, useState } from 'react'

type FormManagementAnalyticsProps = {
  workspaceData: FormManagementWorkspaceData
  onAnalyticsExport?: (file: AnalyticsExportFile) => void
  exportJobClient?: AnalyticsExportJobClient
}

const toneClasses: Record<FormManagementMetricTone, string> = {
  sky: 'bg-sky-50 text-sky-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  indigo: 'bg-indigo-50 text-indigo-700',
  violet: 'bg-violet-50 text-violet-700',
  amber: 'bg-amber-50 text-amber-700',
}

const analyticsExportFormats: Array<{
  id: AnalyticsExportFormat
  label: string
  icon: typeof FileSpreadsheet
}> = [
  { id: 'csv', label: 'CSV', icon: FileSpreadsheet },
  { id: 'excel', label: 'Excel', icon: FileSpreadsheet },
  { id: 'json', label: 'JSON', icon: FileJson },
]

type AnalyticsExportRangeId = AnalyticsExportRequestRangeId

const analyticsExportRanges: Array<{
  id: AnalyticsExportRangeId
  label: string
}> = [
  { id: '7d', label: '最近 7 天' },
  { id: '30d', label: '最近 30 天' },
  { id: '90d', label: '最近 90 天' },
  { id: 'custom', label: '自訂日期' },
]

type LastAnalyticsExport = {
  file: AnalyticsExportFile
  generatedAt: Date
  rangeLabel: string
}

export function FormManagementAnalytics({
  workspaceData,
  onAnalyticsExport,
  exportJobClient = defaultAnalyticsExportJobClient,
}: FormManagementAnalyticsProps) {
  const maxViews = Math.max(...workspaceData.trend.map((point) => point.views), 1)
  const hasQuestionCharts = workspaceData.questionCharts.length > 0
  const [lastAnalyticsExport, setLastAnalyticsExport] = useState<LastAnalyticsExport | null>(null)
  const [analyticsExportHistory, setAnalyticsExportHistory] = useState<LastAnalyticsExport[]>([])
  const [analyticsExportRangeId, setAnalyticsExportRangeId] =
    useState<AnalyticsExportRangeId>('30d')
  const [analyticsCustomRange, setAnalyticsCustomRange] = useState({
    from: '',
    to: '',
  })
  const [analyticsExportRangeError, setAnalyticsExportRangeError] = useState<string | null>(null)
  const [analyticsExportJobs, setAnalyticsExportJobs] = useState<AnalyticsExportJobSafeResponse[]>(
    [],
  )
  const [analyticsExportJobStatus, setAnalyticsExportJobStatus] = useState<string | null>(null)
  const [analyticsExportJobError, setAnalyticsExportJobError] = useState<string | null>(null)
  const [analyticsExportJobLoading, setAnalyticsExportJobLoading] = useState(false)
  const [analyticsExportJobDownloadCheckingId, setAnalyticsExportJobDownloadCheckingId] = useState<
    string | null
  >(null)
  const isApiExportMode = isApiDataClientMode()
  const analyticsExportFormId = workspaceData.snapshotPreview.formId
  const analyticsExportRangeLabel = getAnalyticsExportRangeLabel(
    analyticsExportRangeId,
    analyticsCustomRange,
  )
  const analyticsExportRangeValidationError = getAnalyticsExportRangeValidationError(
    analyticsExportRangeId,
    analyticsCustomRange,
  )
  const analyticsNormalizedRangeSummary = getAnalyticsNormalizedRangeSummary(
    analyticsExportRangeId,
    analyticsCustomRange,
    analyticsExportRangeLabel,
    analyticsExportRangeValidationError,
  )
  const analyticsRoleScopeSummary = getAnalyticsRoleScopeSummary(workspaceData)
  const analyticsQueryFilterSummary = getAnalyticsQueryFilterSummary()
  const analyticsExportPayloadPreview = buildAnalyticsExportRequest({
    format: 'csv',
    range: {
      id: analyticsExportRangeId,
      label: analyticsExportRangeLabel,
    },
    scope: {
      id: 'all',
      label: 'all（mock-safe 管理員可見全部聚合資料）',
    },
    status: {
      id: 'complete',
      label: 'complete（只匯出完成送出的聚合統計）',
    },
    customerWorkflow: {
      id: 'all',
      label: 'all（包含新客戶建檔與既有客戶變更）',
    },
    redactionPolicy: ['aggregate_only', 'pii_format_status_only', 'samples_not_exported'],
  })
  const analyticsExportResponseGeneratedAt = useMemo(() => new Date(), [])
  const analyticsPreviewFile = useMemo(
    () =>
      hasQuestionCharts
        ? buildAnalyticsExportFile({
            format: 'csv',
            formTitle: workspaceData.snapshotPreview.formTitle,
            questionCharts: workspaceData.questionCharts,
          })
        : null,
    [hasQuestionCharts, workspaceData.questionCharts, workspaceData.snapshotPreview.formTitle],
  )
  const analyticsExportResponsePreview = buildAnalyticsExportResponsePreview({
    request: analyticsExportPayloadPreview,
    rowCount: analyticsPreviewFile?.rowCount ?? 0,
    generatedAt: analyticsExportResponseGeneratedAt,
    downloadMode: 'browser_download',
  })
  const analyticsExportAuditEventPreview = buildAnalyticsExportAuditEventPreview({
    responsePreview: analyticsExportResponsePreview,
    actorScope: 'all',
    requestKind: 'aggregate_export',
    providerState: workspaceData.exportJobPreview.providerState,
    persistedState: 'not_persisted_mock_safe',
  })
  const analyticsExportDownloadStatePreview = buildAnalyticsExportDownloadStatePreview({
    file: analyticsPreviewFile
      ? {
          fileName: analyticsPreviewFile.fileName,
          mimeType: analyticsPreviewFile.mimeType,
          rowCount: analyticsPreviewFile.rowCount,
        }
      : null,
    downloadMode: analyticsExportResponsePreview.payload.downloadMode,
    readyState: analyticsPreviewFile ? 'ready_for_browser_download' : 'not_ready',
    providerState: workspaceData.exportJobPreview.providerState,
    retentionPolicy: 'not_persisted_mock_safe',
  })
  const analyticsExportErrorStatePreview = buildAnalyticsExportErrorStatePreview({
    errorCode: 'none_mock_safe',
    errorSurface: 'analytics_export_panel',
    retryPolicy: 'manual_retry_allowed',
    providerState: workspaceData.exportJobPreview.providerState,
    auditState: 'not_persisted_mock_safe',
    safeMessage: '目前沒有匯出錯誤；正式失敗時只顯示安全訊息，不顯示填答明細或個資。',
  })

  async function handleAnalyticsExport(format: AnalyticsExportFormat) {
    if (!hasQuestionCharts) return

    const rangeValidationError = analyticsExportRangeValidationError
    if (rangeValidationError) {
      setAnalyticsExportRangeError(rangeValidationError)
      return
    }

    setAnalyticsExportRangeError(null)
    setAnalyticsExportJobError(null)
    setAnalyticsExportJobStatus(null)

    if (isApiExportMode) {
      setAnalyticsExportJobLoading(true)
      try {
        const job = await exportJobClient.queueAnalyticsExportJob(
          analyticsExportFormId,
          buildAnalyticsExportServerRequest(format, analyticsExportRangeId, analyticsCustomRange),
        )
        setAnalyticsExportJobs((currentJobs) => upsertAnalyticsExportJob(job, currentJobs))
        setAnalyticsExportJobStatus(
          `D1 ${getAnalyticsExportFormatLabel(format)} 匯出工作已建立，狀態 ${getAnalyticsExportJobStatusLabel(
            job.status,
          )}，檔名 ${job.fileName}。`,
        )
      } catch (error) {
        setAnalyticsExportJobError(getAnalyticsExportJobSafeError(error))
      } finally {
        setAnalyticsExportJobLoading(false)
      }
      return
    }

    const generatedAt = new Date()
    const file = buildAnalyticsExportFile({
      format,
      formTitle: workspaceData.snapshotPreview.formTitle,
      questionCharts: workspaceData.questionCharts,
      generatedAt,
    })
    const exportRecord = {
      file,
      generatedAt,
      rangeLabel: analyticsExportRangeLabel,
    }

    setLastAnalyticsExport(exportRecord)
    setAnalyticsExportHistory((currentHistory) => [exportRecord, ...currentHistory].slice(0, 3))

    if (onAnalyticsExport) {
      onAnalyticsExport(file)
      return
    }

    downloadAnalyticsExportFile(file)
  }

  async function handleRefreshAnalyticsExportJobs() {
    setAnalyticsExportJobLoading(true)
    setAnalyticsExportJobError(null)
    setAnalyticsExportJobStatus(null)
    try {
      const result = await exportJobClient.listAnalyticsExportJobs(analyticsExportFormId)
      setAnalyticsExportJobs(result.jobs)
      setAnalyticsExportJobStatus(`已重新整理 D1 匯出工作，共 ${result.jobs.length} 筆。`)
    } catch (error) {
      setAnalyticsExportJobError(getAnalyticsExportJobSafeError(error))
    } finally {
      setAnalyticsExportJobLoading(false)
    }
  }

  async function handleCheckAnalyticsExportDownload(job: AnalyticsExportJobSafeResponse) {
    setAnalyticsExportJobDownloadCheckingId(job.exportJobId)
    setAnalyticsExportJobError(null)
    setAnalyticsExportJobStatus(null)
    try {
      const readiness = await exportJobClient.getAnalyticsExportJobDownloadReadiness(
        analyticsExportFormId,
        job.exportJobId,
      )
      if (readiness.ready) {
        setAnalyticsExportJobs((currentJobs) => upsertAnalyticsExportJob(readiness.job, currentJobs))
        setAnalyticsExportJobStatus(`匯出檔案已可下載：${readiness.job.fileName}。`)
      } else {
        setAnalyticsExportJobStatus(getAnalyticsExportDownloadReadinessMessage(readiness))
      }
    } catch (error) {
      setAnalyticsExportJobError(getAnalyticsExportJobSafeError(error))
    } finally {
      setAnalyticsExportJobDownloadCheckingId(null)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>Mock-safe</Badge>
        <Badge variant="outline">analytics contract preview</Badge>
        <Badge variant={workspaceData.panelStates.analytics === 'ready' ? 'success' : 'warning'}>
          {workspaceData.panelStates.analytics}
        </Badge>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {workspaceData.metrics.map((metric) => (
          <Card key={metric.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm text-slate-500">{metric.label}</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-950">{metric.value}</div>
                </div>
                <div className={`rounded-md px-2 py-1 text-xs ${toneClasses[metric.tone]}`}>
                  {metric.trendLabel}
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-500">{metric.helper}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-5">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-slate-950">回覆趨勢</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Synthetic 近 7 日瀏覽、開始與完成趨勢。後端接線後才會讀取真實 visitor events。
                </p>
              </div>
              <TrendingUp className="size-5 text-indigo-600" />
            </div>
            <div className="mt-5 flex h-56 items-end gap-3 overflow-x-auto rounded-md bg-slate-50 p-4">
              {workspaceData.trend.map((point) => (
                <div key={point.label} className="flex min-w-16 flex-1 flex-col items-center gap-2">
                  <div className="flex h-40 w-full items-end justify-center gap-1">
                    <div
                      className="w-3 rounded-t bg-sky-400"
                      style={{ height: `${Math.max(10, (point.views / maxViews) * 100)}%` }}
                      title={`瀏覽 ${point.views}`}
                    />
                    <div
                      className="w-3 rounded-t bg-indigo-500"
                      style={{ height: `${Math.max(10, (point.starts / maxViews) * 100)}%` }}
                      title={`開始 ${point.starts}`}
                    />
                    <div
                      className="w-3 rounded-t bg-emerald-500"
                      style={{ height: `${Math.max(10, (point.completions / maxViews) * 100)}%` }}
                      title={`完成 ${point.completions}`}
                    />
                  </div>
                  <div className="text-xs text-slate-500">{point.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
              <span className="rounded bg-sky-50 px-2 py-1 text-sky-700">瀏覽量</span>
              <span className="rounded bg-indigo-50 px-2 py-1 text-indigo-700">開始數</span>
              <span className="rounded bg-emerald-50 px-2 py-1 text-emerald-700">完成數</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-slate-950">聚合統計匯出</h3>
                  <Badge variant="outline">analytics aggregate export preview</Badge>
                  <Badge>Mock-safe</Badge>
                </div>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                  匯出目前逐題圖表的聚合 rows，可供 CSV、Excel 或 JSON 檢查；不包含 raw PII
                  samples，也不會啟動後端 export job。
                </p>
                {!hasQuestionCharts ? (
                  <p className="mt-2 text-sm font-medium text-amber-700">
                    目前沒有逐題圖表可匯出。
                  </p>
                ) : null}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1 rounded bg-slate-50 px-2 py-1 text-slate-700">
                <Download className="size-3.5" />
                aggregate only
              </span>
              <span className="rounded bg-emerald-50 px-2 py-1 text-emerald-700">
                不包含 raw PII samples
              </span>
              <span className="rounded bg-indigo-50 px-2 py-1 text-indigo-700">
                mock-safe UI wiring
              </span>
            </div>
            <fieldset
              className="mt-4 rounded-md border border-slate-200 bg-white px-3 py-3"
              aria-label="匯出日期範圍"
            >
              <legend className="flex items-center gap-1.5 px-1 text-xs font-medium text-slate-500">
                <CalendarDays className="size-3.5" />
                匯出日期範圍
              </legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {analyticsExportRanges.map((range) => {
                  const isSelected = analyticsExportRangeId === range.id
                  return (
                    <Button
                      key={range.id}
                      type="button"
                      size="sm"
                      variant={isSelected ? 'secondary' : 'outline'}
                      aria-pressed={isSelected}
                      onClick={() => {
                        setAnalyticsExportRangeId(range.id)
                        setAnalyticsExportRangeError(null)
                      }}
                    >
                      {range.label}
                    </Button>
                  )
                })}
              </div>
              {analyticsExportRangeId === 'custom' ? (
                <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                  <label className="grid gap-1 text-slate-600">
                    <span className="text-xs font-medium">自訂起日</span>
                    <input
                      type="date"
                      value={analyticsCustomRange.from}
                      aria-invalid={analyticsExportRangeError ? true : undefined}
                      aria-describedby={
                        analyticsExportRangeError ? 'analytics-export-range-error' : undefined
                      }
                      onChange={(event) => {
                        const from = event.currentTarget.value
                        setAnalyticsExportRangeError(null)
                        setAnalyticsCustomRange((current) => ({
                          ...current,
                          from,
                        }))
                      }}
                      className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    />
                  </label>
                  <label className="grid gap-1 text-slate-600">
                    <span className="text-xs font-medium">自訂迄日</span>
                    <input
                      type="date"
                      value={analyticsCustomRange.to}
                      aria-invalid={analyticsExportRangeError ? true : undefined}
                      aria-describedby={
                        analyticsExportRangeError ? 'analytics-export-range-error' : undefined
                      }
                      onChange={(event) => {
                        const to = event.currentTarget.value
                        setAnalyticsExportRangeError(null)
                        setAnalyticsCustomRange((current) => ({
                          ...current,
                          to,
                        }))
                      }}
                      className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    />
                  </label>
                </div>
              ) : null}
              {analyticsExportRangeError ? (
                <p
                  id="analytics-export-range-error"
                  role="alert"
                  className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800"
                >
                  {analyticsExportRangeError}
                </p>
              ) : null}
              <p className="mt-3 text-sm font-medium text-slate-700">
                目前範圍：{analyticsExportRangeLabel}
              </p>
            </fieldset>
            <div className="mt-4 flex flex-wrap gap-2">
              {analyticsExportFormats.map((format) => {
                const Icon = format.icon
                return (
                  <Button
                    key={format.id}
                    type="button"
                    variant="outline"
                    onClick={() => void handleAnalyticsExport(format.id)}
                    disabled={!hasQuestionCharts || analyticsExportJobLoading}
                  >
                    <Icon className="size-4" />
                    匯出聚合 {format.label}
                  </Button>
                )
              })}
            </div>
            {isApiExportMode ? (
              <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50/50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-950">D1 匯出工作</h4>
                    <p className="mt-1 text-sm text-slate-600">
                      API mode 會建立後端 export job；下載在 R2 產檔完成前會安全顯示未準備完成。
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={analyticsExportJobLoading}
                    onClick={() => void handleRefreshAnalyticsExportJobs()}
                  >
                    重新整理 D1 匯出工作
                  </Button>
                </div>
                {analyticsExportJobError ? (
                  <p
                    role="alert"
                    className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800"
                  >
                    {analyticsExportJobError}
                  </p>
                ) : null}
                {analyticsExportJobStatus ? (
                  <output
                    role="status"
                    aria-live="polite"
                    className="mt-3 block rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                  >
                    {analyticsExportJobStatus}
                  </output>
                ) : null}
                <div className="mt-3 overflow-x-auto rounded-md border border-slate-200 bg-white">
                  <Table aria-label="D1 analytics export jobs" className="min-w-[760px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>狀態</TableHead>
                        <TableHead>格式</TableHead>
                        <TableHead>列數</TableHead>
                        <TableHead>檔名</TableHead>
                        <TableHead>下載模式</TableHead>
                        <TableHead>操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analyticsExportJobs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-slate-500">
                            尚未建立 D1 匯出工作
                          </TableCell>
                        </TableRow>
                      ) : (
                        analyticsExportJobs.map((job) => (
                          <TableRow key={job.exportJobId}>
                            <TableCell className="font-medium text-slate-900">
                              {getAnalyticsExportJobStatusLabel(job.status)}
                            </TableCell>
                            <TableCell>{getAnalyticsExportFormatLabel(job.format)}</TableCell>
                            <TableCell>{job.rowCount} 列</TableCell>
                            <TableCell className="break-all text-slate-600">
                              {job.fileName}
                            </TableCell>
                            <TableCell>{getAnalyticsExportDownloadModeLabel(job.downloadMode)}</TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={analyticsExportJobDownloadCheckingId === job.exportJobId}
                                onClick={() => void handleCheckAnalyticsExportDownload(job)}
                              >
                                檢查下載狀態
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : null}
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">normalized range 摘要</h4>
                <Badge
                  variant={
                    analyticsNormalizedRangeSummary.validationStatus === 'valid'
                      ? 'success'
                      : 'warning'
                  }
                >
                  {analyticsNormalizedRangeSummary.validationStatus}
                </Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe normalized range preview table"
                  className="min-w-[520px]"
                >
                  <TableBody>
                    {analyticsNormalizedRangeSummary.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-40 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">role-scope 摘要</h4>
                <Badge variant="outline">no widening</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe role-scope preview table"
                  className="min-w-[560px]"
                >
                  <TableBody>
                    {analyticsRoleScopeSummary.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-44 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">query-filter 摘要</h4>
                <Badge variant="outline">excludes tests</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe query-filter preview table"
                  className="min-w-[600px]"
                >
                  <TableBody>
                    {analyticsQueryFilterSummary.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-48 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">export-payload preview</h4>
                <Badge variant="outline">sanitized</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe export-payload preview table"
                  className="min-w-[640px]"
                >
                  <TableBody>
                    {analyticsExportPayloadPreview.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-48 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">server-response preview</h4>
                <Badge variant="outline">metadata only</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe server-response preview table"
                  className="min-w-[680px]"
                >
                  <TableBody>
                    {analyticsExportResponsePreview.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-48 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">audit-event preview</h4>
                <Badge variant="outline">not persisted</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe audit-event preview table"
                  className="min-w-[720px]"
                >
                  <TableBody>
                    {analyticsExportAuditEventPreview.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-48 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">download-state preview</h4>
                <Badge variant="outline">browser download</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe download-state preview table"
                  className="min-w-[720px]"
                >
                  <TableBody>
                    {analyticsExportDownloadStatePreview.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-48 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <h4 className="text-sm font-semibold text-slate-950">error-state preview</h4>
                <Badge variant="outline">safe message only</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table
                  aria-label="聚合統計匯出 mock-safe error-state preview table"
                  className="min-w-[720px]"
                >
                  <TableBody>
                    {analyticsExportErrorStatePreview.rows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell className="w-48 font-medium text-slate-700">
                          {row.field}
                        </TableCell>
                        <TableCell className="text-slate-900">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <dl className="mt-4 grid gap-3 text-sm md:grid-cols-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <dt className="text-xs font-medium text-slate-500">匯出列數預覽</dt>
                <dd className="mt-1 font-semibold text-slate-950">
                  {analyticsPreviewFile ? `${analyticsPreviewFile.rowCount} 列` : '0 列'}
                </dd>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <dt className="text-xs font-medium text-slate-500">檔名預覽</dt>
                <dd className="mt-1 break-all font-semibold text-slate-950">
                  {analyticsPreviewFile?.fileName ?? '尚無檔案'}
                </dd>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <dt className="text-xs font-medium text-slate-500">最近匯出時間</dt>
                <dd className="mt-1 font-semibold text-slate-950">
                  {lastAnalyticsExport
                    ? formatAnalyticsExportDisplayTime(lastAnalyticsExport.generatedAt)
                    : '尚未匯出'}
                </dd>
              </div>
            </dl>
            {lastAnalyticsExport ? (
              <output
                aria-live="polite"
                className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
              >
                聚合 {getAnalyticsExportFormatLabel(lastAnalyticsExport.file.format)}{' '}
                匯出已準備完成，{lastAnalyticsExport.file.rowCount} 列，檔案{' '}
                {lastAnalyticsExport.file.fileName}。範圍：{lastAnalyticsExport.rangeLabel}。不包含
                raw PII samples，mock-safe。
              </output>
            ) : null}
            <div className="mt-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-950">最近匯出紀錄</h4>
                <Badge variant="outline">最近 3 次</Badge>
              </div>
              {analyticsExportHistory.length === 0 ? (
                <p className="mt-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                  尚無匯出紀錄
                </p>
              ) : (
                <div className="mt-2 rounded-md border border-slate-200">
                  <Table aria-label="最近 3 次 mock-safe 匯出紀錄" className="min-w-[640px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>格式</TableHead>
                        <TableHead>範圍</TableHead>
                        <TableHead>列數</TableHead>
                        <TableHead>檔名</TableHead>
                        <TableHead>匯出時間</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analyticsExportHistory.map((item) => (
                        <TableRow key={`${item.file.fileName}-${item.generatedAt.toISOString()}`}>
                          <TableCell className="font-medium text-slate-900">
                            {getAnalyticsExportFormatLabel(item.file.format)}
                          </TableCell>
                          <TableCell>{item.rangeLabel}</TableCell>
                          <TableCell>{item.file.rowCount} 列</TableCell>
                          <TableCell className="break-all text-slate-600">
                            {item.file.fileName}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-slate-600">
                            {formatAnalyticsExportDisplayTime(item.generatedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <BarChart3 className="size-5 text-indigo-600" />
              <h3 className="font-semibold text-slate-950">題目流失率</h3>
            </div>
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>題目</TableHead>
                    <TableHead>到達</TableHead>
                    <TableHead>流失率</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workspaceData.dropoffRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium text-slate-800">{row.question}</TableCell>
                      <TableCell>{row.reached}</TableCell>
                      <TableCell>
                        <div className="min-w-32">
                          <div className="mb-1 text-xs text-slate-500">{row.dropoffRate}%</div>
                          <Progress value={row.dropoffRate} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <PieChart className="size-5 text-violet-600" />
              <h3 className="font-semibold text-slate-950">題型統計摘要</h3>
            </div>
            <div className="mt-4 space-y-3">
              {workspaceData.questionStats.map((item) => (
                <div key={item.id} className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-slate-900">{item.question}</div>
                    <Badge variant="secondary">{item.typeLabel}</Badge>
                  </div>
                  <div className="mt-2 grid gap-2 text-sm text-slate-500 md:grid-cols-3">
                    <span>{item.responses} 份回答</span>
                    <span>{item.topResult}</span>
                    <span>完成率 {item.completionRate}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <FormManagementQuestionCharts charts={workspaceData.questionCharts} />
    </div>
  )
}

function getAnalyticsExportFormatLabel(format: AnalyticsExportFormat) {
  return analyticsExportFormats.find((item) => item.id === format)?.label ?? format
}

function buildAnalyticsExportServerRequest(
  format: AnalyticsExportFormat,
  rangeId: AnalyticsExportRangeId,
  customRange: { from: string; to: string },
): AnalyticsExportServerRequest {
  return {
    format,
    range:
      rangeId === 'custom'
        ? {
            kind: 'custom',
            from: customRange.from,
            to: customRange.to,
          }
        : {
            kind: rangeId,
          },
    status: 'complete',
    customerWorkflow: 'all',
  }
}

function upsertAnalyticsExportJob(
  job: AnalyticsExportJobSafeResponse,
  currentJobs: AnalyticsExportJobSafeResponse[],
) {
  return [job, ...currentJobs.filter((currentJob) => currentJob.exportJobId !== job.exportJobId)]
    .slice(0, 5)
}

function getAnalyticsExportJobStatusLabel(status: AnalyticsExportJobSafeResponse['status']) {
  const labels: Record<AnalyticsExportJobSafeResponse['status'], string> = {
    queued: '已排程',
    processing: '處理中',
    ready: '可下載',
    failed: '失敗',
    expired: '已過期',
  }
  return labels[status] ?? status
}

function getAnalyticsExportDownloadModeLabel(
  downloadMode: AnalyticsExportJobSafeResponse['downloadMode'],
) {
  if (downloadMode === 'worker_stream') return 'Worker 串流'
  if (downloadMode === 'signed_url') return '短效連結'
  return '瀏覽器下載'
}

function getAnalyticsExportJobSafeError(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = String((error as { code?: unknown }).code ?? 'unknown_error')
    return `D1 匯出工作暫時無法完成，安全代碼 ${code}。`
  }
  return 'D1 匯出工作暫時無法完成，請稍後再試。'
}

function getAnalyticsExportDownloadReadinessMessage(
  readiness: Exclude<AnalyticsExportDownloadReadiness, { ready: true }>,
) {
  if (
    readiness.code === 'provider_not_configured' &&
    readiness.meta?.r2_read === 'not_configured'
  ) {
    return `匯出檔案已標記 ready，但下載 provider 尚未設定，狀態碼 ${readiness.status}，安全代碼 ${readiness.code}。`
  }

  return `匯出檔案尚未準備完成，狀態碼 ${readiness.status}，安全代碼 ${readiness.code}。`
}

function getAnalyticsExportRangeLabel(
  rangeId: AnalyticsExportRangeId,
  customRange: { from: string; to: string },
) {
  if (rangeId !== 'custom') {
    return analyticsExportRanges.find((item) => item.id === rangeId)?.label ?? '最近 30 天'
  }

  if (customRange.from && customRange.to) return `${customRange.from} 至 ${customRange.to}`
  if (customRange.from) return `${customRange.from} 起`
  if (customRange.to) return `至 ${customRange.to}`
  return '自訂日期（未設定）'
}

function getAnalyticsExportRangeValidationError(
  rangeId: AnalyticsExportRangeId,
  customRange: { from: string; to: string },
) {
  if (rangeId !== 'custom') return null
  if (!customRange.from || !customRange.to) return '請先填寫完整自訂日期範圍'
  if (customRange.from > customRange.to) return '自訂起日不可晚於自訂迄日'
  return null
}

function getAnalyticsNormalizedRangeSummary(
  rangeId: AnalyticsExportRangeId,
  customRange: { from: string; to: string },
  rangeLabel: string,
  validationError: string | null,
) {
  const isCustomRange = rangeId === 'custom'
  const fixedRangeValue = 'server-derived（固定範圍由 API 計算）'
  const validationStatus = validationError ? 'invalid' : 'valid'

  return {
    validationStatus,
    rows: [
      { field: 'range id', value: rangeId },
      { field: 'range label', value: rangeLabel },
      { field: 'from', value: isCustomRange ? customRange.from || 'missing' : fixedRangeValue },
      { field: 'to', value: isCustomRange ? customRange.to || 'missing' : fixedRangeValue },
      { field: 'validation status', value: validationStatus },
      {
        field: 'mock-safe 說明',
        value: '前端摘要，不查 D1，不寫入匯出檔',
      },
    ],
  }
}

function getAnalyticsRoleScopeSummary(workspaceData: FormManagementWorkspaceData) {
  const permissionRequired = workspaceData.exportJobPreview.permissionRequired

  return {
    rows: [
      { field: 'scope id', value: 'all' },
      { field: 'scope label', value: 'all（mock-safe 管理員可見全部聚合資料）' },
      { field: 'permission source', value: `${permissionRequired}（mock-safe 權限來源）` },
      { field: 'permission widening', value: 'no（不放大目前角色權限）' },
      {
        field: 'provider state',
        value: workspaceData.exportJobPreview.providerState,
      },
      {
        field: 'mock-safe 說明',
        value: '前端摘要，不查 D1，不寫入匯出檔',
      },
    ],
  }
}

function getAnalyticsQueryFilterSummary() {
  return {
    rows: [
      { field: 'status', value: 'complete（只匯出完成送出的聚合統計）' },
      { field: 'customerWorkflow', value: 'all（包含新客戶建檔與既有客戶變更）' },
      { field: 'excluded data', value: 'preview/test/demo/synthetic' },
      { field: 'filter source', value: 'mock-safe front-end summary' },
      { field: 'server validation', value: 'pending（正式 API 需重新驗證）' },
      {
        field: 'mock-safe 說明',
        value: '前端摘要，不查 D1，不寫入匯出檔',
      },
    ],
  }
}

function formatAnalyticsExportDisplayTime(value: Date) {
  const parts = [
    value.getUTCFullYear(),
    '-',
    String(value.getUTCMonth() + 1).padStart(2, '0'),
    '-',
    String(value.getUTCDate()).padStart(2, '0'),
    ' ',
    String(value.getUTCHours()).padStart(2, '0'),
    ':',
    String(value.getUTCMinutes()).padStart(2, '0'),
    ':',
    String(value.getUTCSeconds()).padStart(2, '0'),
    ' UTC',
  ]
  return parts.join('')
}
